// frontend/src/pages/admin/Events.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useCouple } from '../../contexts/CoupleContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import EventService from '../../services/event.service';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import { Calendar, Plus, Edit2, Trash2, Clock, MapPin } from 'react-feather';
import { Event } from '../../types';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const Events: React.FC = () => {
  const { currentCouple } = useCouple();
  
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  
  // Form state
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [formLoading, setFormLoading] = useState<boolean>(false);
  
  useEffect(() => {
    if (currentCouple) {
      fetchEvents();
    }
  }, [currentCouple]);
  
  const fetchEvents = async () => {
    if (!currentCouple) return;
    
    try {
      setLoading(true);
      const fetchedEvents = await EventService.getCoupleEvents(currentCouple._id);
      
      // Sort events by date
      const sortedEvents = fetchedEvents.sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      setEvents(sortedEvents);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch events';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddEvent = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!currentCouple || !date) return;
    
    setFormLoading(true);
    
    try {
      const eventData = {
        title,
        description: description || undefined,
        date: date.toISOString(),
        time: time || undefined,
        location: location || undefined,
        address: address || undefined,
        couple: currentCouple._id
      };
      
      await EventService.createEvent(eventData);
      resetForm();
      setIsAddModalOpen(false);
      fetchEvents();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add event';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleEditEvent = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!currentCouple || !currentEvent || !date) return;
    
    setFormLoading(true);
    
    try {
      const eventData = {
        title,
        description: description || undefined,
        date: date.toISOString(),
        time: time || undefined,
        location: location || undefined,
        address: address || undefined
      };
      
      await EventService.updateEvent(currentEvent._id, eventData);
      resetForm();
      setIsEditModalOpen(false);
      fetchEvents();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update event';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleDeleteEvent = async () => {
    if (!currentEvent) return;
    
    setFormLoading(true);
    
    try {
      await EventService.deleteEvent(currentEvent._id);
      setIsDeleteModalOpen(false);
      fetchEvents();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete event';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };
  
  const openEditModal = (event: Event) => {
    setCurrentEvent(event);
    setTitle(event.title);
    setDescription(event.description || '');
    setDate(new Date(event.date));
    setTime(event.time || '');
    setLocation(event.location || '');
    setAddress(event.address || '');
    setIsEditModalOpen(true);
  };
  
  const openDeleteModal = (event: Event) => {
    setCurrentEvent(event);
    setIsDeleteModalOpen(true);
  };
  
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDate(null);
    setTime('');
    setLocation('');
    setAddress('');
    setCurrentEvent(null);
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
          <p className="text-center text-gray-500">Please create a couple profile first.</p>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Wedding Events</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage your wedding day schedule
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button 
              onClick={() => {
                resetForm();
                setIsAddModalOpen(true);
              }}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Add Event
            </Button>
          </div>
        </div>
        
        {error && (
          <Alert
            type="error"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
            className="mb-6"
          />
        )}
        
        {loading ? (
          <div className="text-center py-12">
            <Loader size="lg" label="Loading events..." />
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No events added yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start adding events to your wedding schedule.
            </p>
            <div className="mt-6">
              <Button 
                onClick={() => {
                  resetForm();
                  setIsAddModalOpen(true);
                }}
              >
                Add Your First Event
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {events.map((event, index) => (
              <div 
                key={event._id} 
                className="bg-white shadow rounded-lg overflow-hidden border-l-4 border-pink-500"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start">
                    <div className="flex-shrink-0 bg-pink-100 rounded-lg p-3 text-center mb-4 md:mb-0 md:mr-6">
                      <div className="text-sm font-medium text-pink-800">
                        {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className="text-2xl font-bold text-pink-800">
                        {new Date(event.date).getDate()}
                      </div>
                      <div className="text-sm font-medium text-pink-800">
                        {new Date(event.date).getFullYear()}
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-medium text-gray-900">{event.title}</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {formatDate(event.date)}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditModal(event)}
                            className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(event)}
                            className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {event.description && (
                        <p className="mt-4 text-sm text-gray-500">{event.description}</p>
                      )}
                      
                      <div className="mt-4 flex flex-col sm:flex-row sm:space-x-6">
                        {event.time && (
                          <div className="flex items-center text-sm text-gray-500 mb-2 sm:mb-0">
                            <Clock className="mr-1.5 h-4 w-4 text-gray-400" />
                            {event.time}
                          </div>
                        )}
                        {event.location && (
                          <div className="flex items-center text-sm text-gray-500 mb-2 sm:mb-0">
                            <MapPin className="mr-1.5 h-4 w-4 text-gray-400" />
                            {event.location}
                          </div>
                        )}
                      </div>
                      
                      {event.address && (
                        <div className="mt-2 text-sm text-gray-500 pl-5">
                          {event.address}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Add Event Modal */}
        <Modal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title="Add Event"
        >
          <form onSubmit={handleAddEvent} className="space-y-4">
            <Input
              id="event-title"
              label="Event Title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Wedding Ceremony"
            />
            
            <Textarea
              id="event-description"
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide additional details about this event"
            />
            
            <div>
              <label htmlFor="event-date" className="block text-sm font-medium text-gray-700">
                Date
              </label>
              <div className="mt-1">
                <DatePicker
                  id="event-date"
                  selected={date}
                  onChange={(date: Date) => setDate(date)}
                  dateFormat="MMMM d, yyyy"
                  className="shadow-sm focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholderText="Select a date"
                  required
                />
              </div>
            </div>
            
            <Input
              id="event-time"
              label="Time (optional)"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="e.g. 4:00 PM"
            />
            
            <Input
              id="event-location"
              label="Location (optional)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Grand Ballroom, Sunset Resort"
            />
            
            <Input
              id="event-address"
              label="Address (optional)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. 123 Main St, City, State, Zip"
            />
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={formLoading}
                disabled={formLoading || !title || !date}
              >
                Add Event
              </Button>
            </div>
          </form>
        </Modal>
        
        {/* Edit Event Modal */}
        <Modal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Event"
        >
          <form onSubmit={handleEditEvent} className="space-y-4">
          // frontend/src/pages/admin/Events.tsx (continued)
            <Input
              id="edit-event-title"
              label="Event Title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Wedding Ceremony"
            />
            
            <Textarea
              id="edit-event-description"
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide additional details about this event"
            />
            
            <div>
              <label htmlFor="edit-event-date" className="block text-sm font-medium text-gray-700">
                Date
              </label>
              <div className="mt-1">
                <DatePicker
                  id="edit-event-date"
                  selected={date}
                  onChange={(date: Date) => setDate(date)}
                  dateFormat="MMMM d, yyyy"
                  className="shadow-sm focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholderText="Select a date"
                  required
                />
              </div>
            </div>
            
            <Input
              id="edit-event-time"
              label="Time (optional)"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="e.g. 4:00 PM"
            />
            
            <Input
              id="edit-event-location"
              label="Location (optional)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Grand Ballroom, Sunset Resort"
            />
            
            <Input
              id="edit-event-address"
              label="Address (optional)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. 123 Main St, City, State, Zip"
            />
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={formLoading}
                disabled={formLoading || !title || !date}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Modal>
        
        {/* Delete Confirmation Modal */}
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Event"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete "{currentEvent?.title}"? This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteEvent}
                loading={formLoading}
                disabled={formLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default Events;